import { useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router";

import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";
import { SpacesContext } from "views/Account/SpacesProvider";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { IntegrationsOutletContext } from "../../types";
import VCSIntegrationsEmpty from "../shared/VcsList/Empty";
import {
  ORIGIN_INTEGRATION_DETAILS_DRAWER_ID,
  ORIGIN_INTEGRATION_NAME,
} from "../shared/VcsList/constants";
import SetupButton from "../shared/VcsList/SetupButton";
import { showVCSDeleteIntegrationModal } from "../shared/VcsShared/components/DeleteIntegrationModal";
import { getEditIntegrationURL } from "../shared/VcsShared/helpers";
import { showVCSIntegrationsOriginDetailsDrawer } from "./components/OriginDetailsDrawer";
import VCSIntegrationsOriginListItem from "./components/OriginListItem";
import useDeleteOriginIntegration from "./useDeleteOriginIntegration";
import useGetOriginIntegration from "./useGetOriginIntegration";

const Origin = () => {
  const navigate = useNavigate();
  const { hasEntityCreateAccess } = useTypedContext(SpacesContext);
  const { integration } = useOutletContext<IntegrationsOutletContext>();

  useTitle(integration.name);
  useLayoutHeading(integration.name);

  const { originIntegration, loading, error } = useGetOriginIntegration();
  const { deleteOriginIntegration } = useDeleteOriginIntegration();

  useLayoutActions(
    !originIntegration && hasEntityCreateAccess ? (
      <SetupButton provider={VcsProvider.Origin} />
    ) : undefined
  );

  const handleEdit = useCallback(() => {
    navigate(getEditIntegrationURL(VcsProvider.Origin, "builtin"));
  }, [navigate]);

  const handleShowDetails = useCallback(() => {
    if (!originIntegration) {
      return;
    }

    showVCSIntegrationsOriginDetailsDrawer({
      id: ORIGIN_INTEGRATION_DETAILS_DRAWER_ID,
      details: originIntegration,
    });
  }, [originIntegration]);

  const handleDelete = useCallback(() => {
    showVCSDeleteIntegrationModal({
      attachedEntitiesQty: 0,
      integrationName: ORIGIN_INTEGRATION_NAME,
      onConfirm: () => deleteOriginIntegration(),
    });
  }, [deleteOriginIntegration]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !originIntegration) {
    return <PageLoading />;
  }

  if (!originIntegration) {
    return <VCSIntegrationsEmpty provider={VcsProvider.Origin} />;
  }

  return (
    <Box role="table" direction="column" grow="1">
      <VCSIntegrationsOriginListItem
        onEdit={handleEdit}
        onShowDetails={handleShowDetails}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default Origin;
